import {
  dehydrate,
  HydrationBoundary,
  QueryClient,
} from "@tanstack/react-query"
import type { Metadata } from "next"
import { notFound } from "next/navigation"
import { CategoryProducstsList } from "@/components/e-commerce/category/products-list"
import { getCategoryProductsInfiniteQueryOptions } from "@/components/e-commerce/category/products-list/query"
import {
  normalizeCategorySearchParams,
  type SearchParamsInput,
} from "@/components/e-commerce/category/query-params/contract"
import { CategorySidebarFilters } from "@/components/e-commerce/category/sidebar"
import { env } from "@/env"
import {
  getCategoryAvailableFilters,
  getCategoryBySlug,
  getCategoryProductsPage,
} from "@/services/woo-commerce-service"

interface CategoryPageProps {
  params: Promise<{ slug: string }>
  searchParams: Promise<SearchParamsInput>
}

const buildCategoryCanonicalPath = (slug: string): string =>
  `/categorias/${slug}`

const hasAnyQueryParam = (searchParams: SearchParamsInput): boolean => {
  for (const value of Object.values(searchParams)) {
    if (Array.isArray(value) && value.length > 0) {
      return true
    }

    if (typeof value === "string" && value.length > 0) {
      return true
    }
  }

  return false
}

export async function generateMetadata({
  params,
  searchParams,
}: {
  params: Promise<{ slug: string }>
  searchParams: Promise<SearchParamsInput>
}): Promise<Metadata> {
  const [{ slug }, resolvedSearchParams] = await Promise.all([
    params,
    searchParams,
  ])
  const category = await getCategoryBySlug(slug)
  const siteUrl = env.NEXT_PUBLIC_SITE_URL ?? "http://localhost:3000"
  const canonicalPath = buildCategoryCanonicalPath(slug)
  const canonicalUrl = `${siteUrl}${canonicalPath}`
  const hasQueryParams = hasAnyQueryParam(resolvedSearchParams)

  if (!category) {
    return {
      metadataBase: new URL(siteUrl),
      title: "Categoria não encontrada",
      description:
        "A categorias solicitada não foi encontrada. Explore outras categorias disponíveis.",
      alternates: {
        canonical: canonicalUrl,
      },
      robots: {
        index: false,
        follow: true,
      },
    }
  }

  return {
    metadataBase: new URL(siteUrl),
    title: `${category.name} | Marketplace Triibo`,
    description: `Confira os produtos de ${category.name} no Marketplace Triibo.`,
    alternates: {
      canonical: canonicalUrl,
    },
    robots: hasQueryParams
      ? {
          index: false,
          follow: true,
        }
      : {
          index: true,
          follow: true,
        },
  }
}

export default async function CategoryPage({
  params,
  searchParams,
}: CategoryPageProps): Promise<React.ReactNode> {
  const [{ slug }, resolvedSearchParams] = await Promise.all([
    params,
    searchParams,
  ])

  const category = await getCategoryBySlug(slug)

  if (!category) {
    notFound()
  }

  const availableFilters = await getCategoryAvailableFilters(slug)
  const normalizedQueryState =
    await normalizeCategorySearchParams(resolvedSearchParams)

  const categoryName =
    slug[0].toLocaleUpperCase() + slug.slice(1).replaceAll("-", " ")

  const categoryProductsQueryKey = JSON.stringify({
    slug,
    per_page: normalizedQueryState.per_page,
    q: normalizedQueryState.q,
    orderby: normalizedQueryState.orderby,
    order: normalizedQueryState.order,
    store_ids: normalizedQueryState.store_ids,
    brand: normalizedQueryState.brand,
    min_rating: normalizedQueryState.min_rating,
    discount: normalizedQueryState.discount !== null,
    min_price: normalizedQueryState.min_price,
    max_price: normalizedQueryState.max_price,
  })

  const loadCategoryPage = async ({
    page,
  }: {
    page: number
  }): Promise<{
    items: Awaited<ReturnType<typeof getCategoryProductsPage>>["products"]
    page: number
    totalPages: number
    totalProducts: number
  }> => {
    "use server"

    const payload = await getCategoryProductsPage({
      slug,
      page,
      per_page: normalizedQueryState.per_page,
      q: normalizedQueryState.q,
      orderby: normalizedQueryState.orderby,
      order: normalizedQueryState.order,
      store_ids: normalizedQueryState.store_ids,
      brand: normalizedQueryState.brand ?? undefined,
      min_rating: normalizedQueryState.min_rating ?? undefined,
      discount: normalizedQueryState.discount !== null,
      min_price: normalizedQueryState.min_price ?? undefined,
      max_price: normalizedQueryState.max_price ?? undefined,
    })

    return {
      items: payload.products,
      page,
      totalPages: payload.totalPages,
      totalProducts: payload.totalProducts,
    }
  }

  const queryClient = new QueryClient()

  await queryClient.prefetchInfiniteQuery(
    getCategoryProductsInfiniteQueryOptions({
      onLoadMoreProducts: loadCategoryPage,
      queryKeySeed: categoryProductsQueryKey,
    })
  )

  return (
    <main className='mx-auto w-full p-6'>
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]'>
        <CategorySidebarFilters availableFilters={availableFilters} />

        <HydrationBoundary state={dehydrate(queryClient)}>
          <CategoryProducstsList
            categoryName={categoryName}
            onLoadMoreProducts={loadCategoryPage}
            queryKeySeed={categoryProductsQueryKey}
          />
        </HydrationBoundary>
      </div>
    </main>
  )
}
